"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAllPayoutsWithData = exports.getExecutePayoutSafeTransaction = exports.createNewSplitPayoutBeneficiary = exports.createNewPayoutData = void 0;
const protocol_kit_1 = __importDefault(require("@safe-global/protocol-kit"));
const axios_1 = __importDefault(require("axios"));
const ethers_1 = require("ethers");
const utils_js_1 = require("ethers/lib/utils.js");
const abis_1 = require("../abis");
const chains_1 = require("./../config/chains");
const ipfs_utils_1 = require("./ipfs.utils");
function truncate(num, fixed) {
    var _a, _b;
    const regex = new RegExp("^-?\\d+(?:.\\d{0," + (fixed || -1) + "})?");
    return (_b = (_a = num.toString().match(regex)) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : num.toString();
}
const createNewPayoutData = (type) => {
    if (type === "single") {
        return {
            type,
            beneficiary: "",
            title: "",
            severity: "",
            percentageToPay: "",
            severityBountyIndex: "",
            explanation: "",
            nftUrl: "",
            additionalInfo: "Submission tx: \n\nSubmission link: \n\nDecrypted submission: ",
        };
    }
    else {
        return {
            type,
            title: "",
            explanation: "",
            additionalInfo: "",
            beneficiaries: [(0, exports.createNewSplitPayoutBeneficiary)()],
        };
    }
};
exports.createNewPayoutData = createNewPayoutData;
const createNewSplitPayoutBeneficiary = () => {
    return {
        beneficiary: "",
        severity: "",
        percentageOfPayout: "",
        nftUrl: "",
    };
};
exports.createNewSplitPayoutBeneficiary = createNewSplitPayoutBeneficiary;
const getExecutePayoutSafeTransaction = async (providerUrl, committee, payout) => {
    var _a, _b;
    const vaultInfo = payout.vaultInfo;
    const protocolKit = await protocol_kit_1.default.init({
        provider: providerUrl,
        safeAddress: committee,
    });
    const percentageToPay = Math.round(Number(payout.payoutData.percentageToPay) * 100);
    if (payout.payoutData.type === "single") {
        // Single payout: only one TX calling the vault contract
        const contractAddress = vaultInfo.version === "v1"
            ? vaultInfo.master
            : vaultInfo.version === "v2"
                ? vaultInfo.address
                : (_a = vaultInfo.claimsManager) !== null && _a !== void 0 ? _a : "";
        const payoutData = payout.payoutData;
        let encodedExecPayoutData = "";
        if (vaultInfo.version === "v1") {
            const contractInterface = new ethers_1.ethers.utils.Interface(abis_1.HATSVaultV1_abi);
            encodedExecPayoutData = contractInterface.encodeFunctionData("pendingApprovalClaim", [
                Number(vaultInfo.pid),
                payoutData.beneficiary,
                Number(payoutData.severityBountyIndex),
            ]);
        }
        else if (vaultInfo.version === "v2") {
            const contractInterface = new ethers_1.ethers.utils.Interface(abis_1.HATSVaultV2_abi);
            encodedExecPayoutData = contractInterface.encodeFunctionData("submitClaim", [
                payoutData.beneficiary,
                percentageToPay,
                payout.payoutDescriptionHash,
            ]);
        }
        else {
            const contractInterface = new ethers_1.ethers.utils.Interface(abis_1.HATSVaultV3ClaimsManager_abi);
            encodedExecPayoutData = contractInterface.encodeFunctionData("submitClaim", [
                payoutData.beneficiary,
                percentageToPay,
                payout.payoutDescriptionHash,
            ]);
        }
        const safeTransaction = await protocolKit.createTransaction({
            transactions: [
                {
                    to: contractAddress,
                    data: encodedExecPayoutData,
                    value: "0",
                },
            ],
        });
        const safeTransactionHash = await protocolKit.getTransactionHash(safeTransaction);
        return { tx: safeTransaction, txHash: safeTransactionHash };
    }
    else {
        // Only works with v2 and v3 vaults
        // Split payout: two TXs with a batch on safe. One to create the payment splitter, and the other to execute the payout.
        // First TX: create payment splitter (this will be the beneficiary of the vault)
        // Second TX: execute payout
        if (vaultInfo.version === "v1")
            throw new Error("Split payouts are only supported for v2/v3 vaults");
        const paymentSplitterFactoryAddress = chains_1.ChainsConfig[Number(vaultInfo.chainId)].paymentSplitterFactory;
        if (!paymentSplitterFactoryAddress)
            throw new Error("Payment splitter factory address not found");
        const vaultContract = {
            address: vaultInfo.version === "v2" ? vaultInfo.address : (_b = vaultInfo.claimsManager) !== null && _b !== void 0 ? _b : "",
            interface: new ethers_1.ethers.utils.Interface(vaultInfo.version === "v2" ? abis_1.HATSVaultV2_abi : abis_1.HATSVaultV3ClaimsManager_abi),
        };
        const paymentSplitterFactoryContract = {
            address: paymentSplitterFactoryAddress,
            interface: new ethers_1.ethers.utils.Interface(abis_1.HATPaymentSplitterFactory_abi),
        };
        const payoutData = payout.payoutData;
        // Join same beneficiaries and sum percentages
        const beneficiariesToIterate = JSON.parse(JSON.stringify(payoutData.beneficiaries));
        // If vault is v3, we will pay 100% of the vault. So we need to add the remaining funds to the depositors and governance
        if (vaultInfo.version === "v3") {
            if (!vaultInfo.hatsGovFee)
                throw new Error(`Hats governance fee not found on vaultInfo for payout id: ${payout._id}`);
            const hatsGovFee = +vaultInfo.hatsGovFee / 100 / 100;
            const totalToPay = percentageToPay * 1;
            const governancePercentage = totalToPay * hatsGovFee;
            const hackersPercentage = totalToPay * (1 - hatsGovFee);
            const depositorsPercentage = 10000 - totalToPay;
            const hackersPoints = beneficiariesToIterate.reduce((acc, beneficiary) => acc + +beneficiary.percentageOfPayout, 0);
            const governancePoints = (governancePercentage * hackersPoints) / hackersPercentage;
            const depositorsPoints = (depositorsPercentage * hackersPoints) / hackersPercentage;
            // Add depositors as beneficiaries
            if (payout.payoutData.depositors && depositorsPercentage > 0) {
                beneficiariesToIterate.push(...payout.payoutData.depositors.map((depositor) => ({
                    beneficiary: depositor.address,
                    severity: "depositor",
                    nftUrl: "",
                    percentageOfPayout: truncate(depositorsPoints * (depositor.ownership / 100), 4),
                })));
            }
            // Add governance ans curator as beneficiary
            // We are doing this because in v3 the govFees on-chain is 0%. We need to calculate it manually
            if (governancePercentage > 0) {
                const govWallet = chains_1.ChainsConfig[Number(vaultInfo.chainId)].govMultisig;
                if (!govWallet)
                    throw new Error(`Gov wallet not found on ChainsConfig for payout id: ${payout._id}`);
                // Add curator as beneficiary
                let remainingGovPoints = governancePoints;
                if (payout.payoutData.curator) {
                    const curatorPercentage = payout.payoutData.curator.percentage / 100;
                    if (curatorPercentage < 0 || curatorPercentage > 50) {
                        throw new Error(`Invalid curator percentage for payout id: ${payout._id} and percentage ${curatorPercentage}`);
                    }
                    const curatorFees = curatorPercentage * remainingGovPoints;
                    remainingGovPoints = remainingGovPoints - curatorFees;
                    beneficiariesToIterate.push({
                        beneficiary: payout.payoutData.curator.address,
                        severity: "curator",
                        nftUrl: "",
                        percentageOfPayout: truncate(curatorFees, 4),
                    });
                }
                beneficiariesToIterate.push({
                    beneficiary: govWallet,
                    severity: "governance",
                    nftUrl: "",
                    percentageOfPayout: truncate(remainingGovPoints, 4),
                });
            }
        }
        const beneficiariesJointPercentage = beneficiariesToIterate
            .reduce((acc, beneficiary) => {
            const existingBeneficiary = acc.find((b) => b.beneficiary.toLowerCase() === beneficiary.beneficiary.toLowerCase());
            if (existingBeneficiary) {
                existingBeneficiary.percentageOfPayout = truncate(+truncate(+existingBeneficiary.percentageOfPayout, 4) + +truncate(+beneficiary.percentageOfPayout, 4), 4);
            }
            else {
                acc.push(beneficiary);
            }
            return acc;
        }, [])
            .map((ben) => (Object.assign(Object.assign({}, ben), { beneficiary: (0, utils_js_1.getAddress)(ben.beneficiary) })));
        // Payout payment splitter creation TX
        const encodedPaymentSplitterCreation = paymentSplitterFactoryContract.interface.encodeFunctionData("createHATPaymentSplitter", [
            beneficiariesJointPercentage.map((beneficiary) => beneficiary.beneficiary),
            beneficiariesJointPercentage.map((beneficiary) => ethers_1.BigNumber.from(Math.round(Number(beneficiary.percentageOfPayout) * 10 ** 10))),
        ]);
        // Payout execution TX
        // If v3, the percentage to pay is always 100%. We will pay to the beneficiaries and the remaining is going to depositors/governance.
        const encodedExecutePayout = vaultContract.interface.encodeFunctionData("submitClaim", [
            payoutData.paymentSplitterBeneficiary,
            vaultInfo.version === "v3" ? 10000 : percentageToPay,
            payout.payoutDescriptionHash,
        ]);
        const safeTransaction = await protocolKit.createTransaction({
            transactions: [
                {
                    to: paymentSplitterFactoryAddress,
                    data: encodedPaymentSplitterCreation,
                    value: "0",
                },
                {
                    to: vaultContract.address,
                    data: encodedExecutePayout,
                    value: "0",
                },
            ],
        });
        const safeTransactionHash = await protocolKit.getTransactionHash(safeTransaction);
        return { tx: safeTransaction, txHash: safeTransactionHash };
    }
};
exports.getExecutePayoutSafeTransaction = getExecutePayoutSafeTransaction;
const getAllPayoutsWithData = async (env = "mainnet") => {
    var _a, _b, _c;
    try {
        const GET_ALL_PAYOUTS = `
      query getPayouts {
        payouts: claims(first: 1000) {
          id
          vault {
            id
          }
          payoutDataHash: claim
          beneficiary: claimer
          approvedAt
          dismissedAt
          bountyPercentage
          severityIndex: severity
          hackerReward
          hackerVestedReward
          governanceHatReward
          hackerHatReward
          committeeReward
          isChallenged
        }
      }
    `;
        const subgraphsRequests = Object.values(chains_1.ChainsConfig)
            .filter((chain) => (env === "testnet" ? chain.chain.testnet : env === "mainnet" ? !chain.chain.testnet : true))
            .map(async (chain) => {
            return {
                chainId: chain.chain.id,
                request: await axios_1.default.post(chain.subgraph, JSON.stringify({
                    query: GET_ALL_PAYOUTS,
                }), {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }),
            };
        });
        const subgraphsResponses = await Promise.allSettled(subgraphsRequests);
        const fulfilledResponses = subgraphsResponses.filter((response) => response.status === "fulfilled");
        const subgraphsData = fulfilledResponses.map((res) => res.value);
        const payouts = [];
        for (let i = 0; i < subgraphsData.length; i++) {
            const chainId = subgraphsData[i].chainId;
            if (!subgraphsData[i].request.data)
                continue;
            if (!((_a = subgraphsData[i].request.data) === null || _a === void 0 ? void 0 : _a.data))
                continue;
            if (!subgraphsData[i].request.data || !((_c = (_b = subgraphsData[i].request.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.payouts))
                continue;
            for (const payout of subgraphsData[i].request.data.data.payouts) {
                payouts.push(Object.assign({ chainId }, payout));
            }
        }
        const loadPayoutDescription = async (payout) => {
            if ((0, ipfs_utils_1.isValidIpfsHash)(payout.payoutDataHash)) {
                try {
                    const dataResponse = await fetch(`https://ipfs2.hats.finance/ipfs/${payout.payoutDataHash}`);
                    if (dataResponse.status === 200) {
                        const object = await dataResponse.json();
                        return object;
                    }
                    return undefined;
                }
                catch (error) {
                    console.error(error);
                    return undefined;
                }
            }
            return undefined;
        };
        // Load descriptions
        const getPayoutsData = async (payoutsToFetch) => Promise.all(payoutsToFetch.map(async (payout) => {
            var _a, _b, _c, _d;
            const payoutData = (await loadPayoutDescription(payout));
            if ((payoutData === null || payoutData === void 0 ? void 0 : payoutData.type) === "single") {
                if (payoutData === null || payoutData === void 0 ? void 0 : payoutData.decryptedSubmission)
                    delete payoutData.decryptedSubmission;
            }
            else {
                for (const beneficiary of (_a = payoutData === null || payoutData === void 0 ? void 0 : payoutData.beneficiaries) !== null && _a !== void 0 ? _a : []) {
                    if (beneficiary === null || beneficiary === void 0 ? void 0 : beneficiary.decryptedSubmission)
                        delete beneficiary.decryptedSubmission;
                }
            }
            const getV3TotalPaidOut = () => {
                var _a, _b, _c, _d;
                const vault = (_a = payout.payoutData) === null || _a === void 0 ? void 0 : _a.vault;
                if (!vault)
                    return undefined;
                if (vault.version !== "v3")
                    return undefined;
                if (Number(payout.bountyPercentage) !== 10000)
                    return undefined;
                const realGovFee = Number(vault.governanceHatRewardSplit) / 100 / 100;
                const realPaidPercentage = Number((_b = payout.payoutData) === null || _b === void 0 ? void 0 : _b.percentageToPay) / 100;
                const paidOut = ethers_1.BigNumber.from((_c = payout.hackerReward) !== null && _c !== void 0 ? _c : "0").add(ethers_1.BigNumber.from((_d = payout.hackerVestedReward) !== null && _d !== void 0 ? _d : "0"));
                const paidOutHackers = Number((0, utils_js_1.formatUnits)(paidOut, vault.stakingTokenDecimals)) * realPaidPercentage * (1 - realGovFee);
                return !!payout.approvedAt ? (0, utils_js_1.parseUnits)(`${paidOutHackers}`, vault.stakingTokenDecimals).toString() : undefined;
            };
            return Object.assign(Object.assign({}, payout), { payoutData, isActive: !payout.dismissedAt && !payout.approvedAt, isApproved: !!payout.approvedAt, isDismissed: !!payout.dismissedAt, totalPaidOut: !!payout.approvedAt
                    ? (_b = getV3TotalPaidOut()) !== null && _b !== void 0 ? _b : ethers_1.BigNumber.from((_c = payout.hackerReward) !== null && _c !== void 0 ? _c : "0")
                        .add(ethers_1.BigNumber.from((_d = payout.hackerVestedReward) !== null && _d !== void 0 ? _d : "0"))
                        .toString()
                    : undefined });
        }));
        const payoutsWithDescription = await getPayoutsData(payouts);
        return payoutsWithDescription;
    }
    catch (error) {
        console.log(error);
        return [];
    }
};
exports.getAllPayoutsWithData = getAllPayoutsWithData;
